import { useEffect, useMemo } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import dayjs from 'dayjs';
import { ScheduleQueries, ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AppointmentStatusComponents } from '@frontend/appointment-status';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useGetCalendarEventInfo } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  SpinningLoader,
  usePopoverMenu,
  NakedButton,
  PopoverMenu,
  PopoverMenuItem,
  useAlert,
} from '@frontend/design-system';
import { useGetSelectedCalendarDateAppointmentInfo } from '../../../hooks/use-get-selected-calendar-date-appointment-info';
import { useAppointmentEventCardShallowStore } from '../../../stores/use-appointment-event-card-store';
import { getLatestUpdatedAppointmentStatus, formatStringForComparison } from '../helpers';
import { useAppointmentStatusShallowStore } from './appointmentStatusStore';
import { AppointmentStatusType, APPOINTMENT_STATUS_MAPPING, AppointmentStatusEnum } from './types';

type AppointmentStatusProps = {
  locationId: string;
  appointmentId: string;
  status: string | undefined;
  isIconOnly?: boolean;
  darkModeMenu?: boolean;
  bordered?: boolean;
  disabled?: boolean;
  className?: string;
  wrapperStyle?: Interpolation<Theme>;
};

export const AppointmentStatusV2 = ({
  locationId,
  appointmentId,
  status,
  isIconOnly = false,
  darkModeMenu = false,
  bordered = false,
  disabled = false,
  className,
  wrapperStyle,
}: AppointmentStatusProps) => {
  const { isIntegratedOffice } = useAppointmentEventCardShallowStore('isIntegratedOffice');

  const { data: dataSourcesData, isLoading: isLoadingDataSources } = SchedulerV3.Helpers.useGetDataSourcesForLocation({
    locationId,
    isEnabled: isIntegratedOffice,
  });

  const isWritebackEnabled = useMemo(
    () =>
      dataSourcesData?.dataSources?.some((dataSource) =>
        dataSource.integration?.capabilities?.find(
          (capability) => capability.dataType === 'APPOINTMENT_STATUS' && capability.operation === 'LIST'
        )
      ),
    [dataSourcesData]
  );

  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarEvents');
  const { appointmentStatus, setAppointmentStatus, resetAppointmentStatus } = useAppointmentStatusShallowStore(
    'appointmentStatus',
    'setAppointmentStatus',
    'resetAppointmentStatus'
  );

  const { data: appointmentStatusData, isLoading: isAppointmentStatusLoading } =
    ScheduleQueries.useGetAppointmentStatuses(locationId);

  const appointmentDetails = useGetSelectedCalendarDateAppointmentInfo(appointmentId);
  // NOTE: not passing startDate & referenceId as its calling for V2 case only
  const {
    eventInfo: appointmentInfo,
    refetch: refetchAppointment,
    isLoading: isAppointmentDataLoading,
    isRefetching: isAppointmentDataRefetching,
  } = useGetCalendarEventInfo({ eventId: appointmentId, locationId, enabled: !appointmentDetails });

  const appointment = appointmentDetails || appointmentInfo;

  const {
    getTriggerProps: getPopoverTriggerProps,
    getMenuProps: getPopoverProps,
    close: closePopover,
    getItemProps: getPopoverItemProps,
  } = usePopoverMenu({ placement: 'bottom-start', darkMode: darkModeMenu });

  const sourceAppointmentStatusMapping = useMemo(() => {
    const source = appointmentStatusData?.mappings?.find(
      (mapping) => mapping.sourceId === appointment?.createdBySourceId
    );

    let mappings = source?.mappings?.filter((status) => status.value.length);

    if (mappings?.length === 0 && source?.externalValues) {
      mappings = source?.externalValues.map((status) => {
        return {
          key: status.label,
          value: [{ label: status.label, value: status.value }],
        };
      });
    }
    return mappings;
  }, [appointmentStatusData, appointment]);

  const appointmentStatuses = useMemo(() => {
    return sourceAppointmentStatusMapping?.reduce((acc: AppointmentStatusType[], curr) => {
      const statusMap = APPOINTMENT_STATUS_MAPPING.get(
        curr.key?.toLowerCase().replace(' ', '') as AppointmentStatusEnum
      );
      curr.value.forEach((assignedStatus) => {
        const statusObject: AppointmentStatusType = {
          key: curr.key,
          value: assignedStatus.value,
          text: assignedStatus.label,
          iconName: statusMap ? statusMap.iconName : 'pending-small',
          variant: statusMap && statusMap.variant,
        };
        acc.push(statusObject);
      });
      return acc;
    }, []);
  }, [sourceAppointmentStatusMapping]);

  const resetAppointmentStatusStore = () => resetAppointmentStatus();

  const currentStatus = useMemo(() => {
    const statusToMatch =
      appointment && appointment.statusOfficeView
        ? formatStringForComparison(appointment?.statusOfficeView)
        : (status && formatStringForComparison(status)) ?? '';

    const statusChipValue = (statusToMatch: string) => {
      const status = appointmentStatuses?.find((statusItem) => {
        return (
          statusToMatch === formatStringForComparison(statusItem.key ?? '') ||
          statusToMatch === formatStringForComparison(statusItem.text ?? '')
        );
      });
      return status;
    };

    return (
      statusChipValue(statusToMatch) ||
      APPOINTMENT_STATUS_MAPPING.get(statusToMatch.replace(' ', '') as AppointmentStatusEnum)
    );
  }, [appointmentStatuses, appointment?.statusOfficeView, status, appointment]);

  const statusChip = useMemo(() => {
    let chip = currentStatus;
    if (appointmentStatus.length) {
      const existsInStore = appointmentStatus.findLast((status) => status.id === appointmentId);

      if (existsInStore) {
        const canUpdate = getLatestUpdatedAppointmentStatus(existsInStore);
        if (canUpdate) {
          resetAppointmentStatusStore();
          chip = currentStatus;
        } else {
          chip = existsInStore;
        }
      }
    }
    return chip as AppointmentStatusType;
  }, [appointmentId, appointmentStatus, currentStatus]);

  useEffect(() => {
    if (appointmentStatus.length) {
      resetAppointmentStatusStore();
    }
  }, []);

  const { mutateAsync: updateAppointmentStatus } = ScheduleQueries.useMutateUpdateAppointmentStatus(
    appointment?.externalId ?? ''
  );

  const updateStatus = async (requestData: AppointmentStatusType) => {
    if (!appointment) return;
    const { value, text, key, variant } = requestData;

    const data = {
      status_id: value,
      status: text,
      source_id: appointment.createdBySourceId ?? '',
      source_type: 'employee',
      location_id: locationId,
    } as ScheduleTypes.AppointmentStatusRequest;

    try {
      await updateAppointmentStatus(data);
      refetchAppointment();
      alert.success(t('Appointment status updated. It might take a few minutes to reflect'));
      setAppointmentStatus({
        key,
        text,
        value,
        iconName: requestData.iconName,
        variant,
        id: appointmentId,
        updatedAt: formatDate(dayjs()),
      });
    } catch (error) {
      console.error('Error updating appointment status', error);
      alert.error(t('Error updating appointment status'));
    } finally {
      closePopover();
    }
  };

  const getExternalStatusCounts = (appointmentStatuses: AppointmentStatusType[]) => {
    return appointmentStatuses.reduce((acc, item) => {
      const externalStatus = formatStringForComparison(item.text) ?? '';
      acc[externalStatus] = (acc[externalStatus] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  };

  if (!appointmentId) return null;

  if (!isWritebackEnabled) {
    const statusInfo = APPOINTMENT_STATUS_MAPPING.get(status?.toLowerCase().replace(' ', '') as AppointmentStatusEnum);

    if (!statusInfo) return null;
    return (
      <AppointmentStatusComponents.Wrapper wrapperStyle={wrapperStyle}>
        <AppointmentStatusComponents.AppointmentStatusChip
          label={statusInfo.text}
          chipVariant={statusInfo?.variant}
          icon={statusInfo?.iconName}
          isIconOnly={isIconOnly}
          bordered={bordered}
          css={{ minWidth: 'fit-content' }}
          color='currentColor'
          className={className}
          disabled={disabled}
        />
      </AppointmentStatusComponents.Wrapper>
    );
  }

  return (
    <>
      {!disabled && !!appointmentStatuses && !!statusChip && (
        <PopoverMenu {...getPopoverProps()}>
          {isAppointmentStatusLoading && <SpinningLoader size='xs' />}
          {appointmentStatuses.map((statusItem, index) => {
            // Key is the Weave status, text is the external status name, and value is the external status id
            const { key, text, value, iconName } = statusItem;

            // Format the key, text, and value from the appointment statuses mapping object to lowercase and trim to ensure the comparison is accurate
            const externalStatusText = formatStringForComparison(text);
            const externalStatusValue = formatStringForComparison(value ?? '');
            const statusKey = formatStringForComparison(key ?? '');

            // Format the Weave and PMS statuses from the appointment to lowercase and trim to ensure the comparison is accurate
            const appointmentStatus = appointment
              ? formatStringForComparison(appointment.statusOfficeView ?? status ?? '')
              : AppointmentStatusEnum.Unknown;

            const appointmentExternalStatus = formatStringForComparison(appointment?.externalStatus ?? '');

            // if the external/PMS status (text) already exist in the mapping object, display `${key} / ${text} - ${value}`. This is needed if the external/PMS status already exists, but the value (external/PMS status id) is different, e.g. "Confirmed / Confirmed - 1".
            const statusCounts = getExternalStatusCounts(appointmentStatuses);

            const getStatusLabel = () => {
              if (statusKey && statusCounts[externalStatusText] > 1) {
                return `${key} / ${text} - ${value}`;
              }
              return `${key} / ${text}`;
            };

            const weaveStatusMatches = appointmentStatus === statusKey || statusKey === externalStatusText;

            // The `externalStatus` in the appointment can be an id OR the text of the PMS status.
            const externalStatusMatches = !!appointmentExternalStatus
              ? externalStatusValue === appointmentExternalStatus || appointmentExternalStatus === externalStatusText
              : true;

            const selectedStatus = weaveStatusMatches && externalStatusMatches;

            return (
              <PopoverMenuItem
                key={`${key}-${value}`}
                value={value}
                {...getPopoverItemProps({ index, onClick: () => updateStatus(statusItem) })}
                css={{
                  borderLeft: selectedStatus ? `4px solid ${theme.colors.primary50}` : `4px solid transparent`,
                }}
                active={statusItem.key === statusChip.key}
              >
                {iconName && <Icon name={iconName} />}
                {getStatusLabel()}
              </PopoverMenuItem>
            );
          })}
        </PopoverMenu>
      )}
      {isAppointmentDataLoading || isAppointmentDataRefetching || isAppointmentStatusLoading || isLoadingDataSources ? (
        <SpinningLoader size={isIconOnly ? 'xxs' : 'xs'} />
      ) : (
        <>
          {!!statusChip && (
            <NakedButton {...getPopoverTriggerProps()}>
              <AppointmentStatusComponents.Wrapper wrapperStyle={wrapperStyle}>
                <AppointmentStatusComponents.AppointmentStatusChip
                  chipVariant={statusChip?.variant}
                  label={statusChip.key ? statusChip.key : statusChip.text}
                  icon={statusChip?.iconName}
                  isIconOnly={isIconOnly}
                  bordered={bordered}
                  color='currentColor'
                  css={{ width: 'auto', maxWidth: '200px' }}
                  className={className}
                  disabled={disabled}
                />
              </AppointmentStatusComponents.Wrapper>
            </NakedButton>
          )}
        </>
      )}
    </>
  );
};
